// src/components/BookingForm.js
import React, { useState } from 'react';
import moment from 'moment';
import { db } from '../firebaseConfig';
import { collection, addDoc, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { FaArrowLeft, FaCalendarAlt, FaClock, FaCheck, FaStickyNote, FaRedo, FaCalendarDay } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { sendNewBookingEmail } from '../services/emailService';
import '../App.css';

const spacesOptions = [
    "Sala Conferenze \"Danilo Dolci\"",
    "Sala Principale",
    "Cortile"
];

const weekDaysOptions = [
    { id: 1, label: 'Lun' },
    { id: 2, label: 'Mar' },
    { id: 3, label: 'Mer' },
    { id: 4, label: 'Gio' },
    { id: 5, label: 'Ven' },
    { id: 6, label: 'Sab' },
    { id: 0, label: 'Dom' }
];

function BookingForm({ user, onBack }) {
    const [eventName, setEventName] = useState('');
    const [selectedSpaces, setSelectedSpaces] = useState([]);
    const [description, setDescription] = useState('');
    const [adminNotes, setAdminNotes] = useState('');

    // MODIFICA: bookingType sostituisce la logica booleana semplice
    // 'single' = Giorno Singolo
    // 'consecutive' = Più giorni consecutivi (ex isMultiDay)
    // 'recurring' = Ricorrente (settimanale)
    const [bookingType, setBookingType] = useState('single');

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedWeekDays, setSelectedWeekDays] = useState([]); // Array di numeri (0-6)

    const [startTime, setStartTime] = useState('');
    const [endTime, setEndTime] = useState('');

    //ACCETTAZIONE TERMINI
    const [termsAccepted, setTermsAccepted] = useState(false);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const toggleSpace = (space) => {
        if (selectedSpaces.includes(space)) {
            setSelectedSpaces(selectedSpaces.filter(s => s !== space));
        } else {
            setSelectedSpaces([...selectedSpaces, space]);
        }
    };

    const toggleWeekDay = (dayId) => {
        if (selectedWeekDays.includes(dayId)) {
            setSelectedWeekDays(selectedWeekDays.filter(d => d !== dayId));
        } else {
            setSelectedWeekDays([...selectedWeekDays, dayId]);
        }
    };

    // Genera l'elenco dei giorni (stringhe 'YYYY-MM-DD') da verificare
    const getSpecificDatesToCheck = (start, end, type, weekDays) => {
        const dates = [];
        if (!start) return dates;

        if (type === 'single') {
            dates.push(start);
            return dates;
        }

        const current = moment(start, 'YYYY-MM-DD');
        const stop = moment(end || start, 'YYYY-MM-DD');

        while (current.isSameOrBefore(stop, 'day')) {
            const dateStr = current.format('YYYY-MM-DD');
            if (type === 'consecutive') {
                dates.push(dateStr);
            } else if (type === 'recurring') {
                if (weekDays.map(Number).includes(current.day())) {
                    dates.push(dateStr);
                }
            }
            current.add(1, 'day');
        }
        return dates;
    };

    const checkAvailability = async () => {
        try {
            // Controlla sia le prenotazioni già confermate che quelle in attesa di approvazione
            const q = query(collection(db, "bookings"), where("status", "in", ["confirmed", "pending"]));
            const querySnapshot = await getDocs(q);

            const myDates = getSpecificDatesToCheck(startDate, endDate, bookingType, selectedWeekDays);
            if (myDates.length === 0) return { hasConflict: false };

            const mySpaces = selectedSpaces.map(s => s.trim());

            for (const docSnap of querySnapshot.docs) {
                const b = docSnap.data();
                if (!b.spaceId) continue;

                // Controlla se condivide uno o più spazi selezionati
                const bookedSpaces = b.spaceId.split(',').map(s => s.trim());
                const commonSpaces = mySpaces.filter(s => bookedSpaces.includes(s));
                if (commonSpaces.length === 0) continue;

                const bStart = b.startDate;
                const bEnd = b.endDate || b.startDate;
                const isBRecurring = (b.bookingType === 'recurring' || (b.recurrenceDays && b.recurrenceDays.length > 0)) && b.recurrenceDays;

                for (const myDate of myDates) {
                    if (myDate >= bStart && myDate <= bEnd) {
                        // Se la data specifica è stata esclusa dall'admin per la prenotazione esistente
                        if (b.excludedDates && b.excludedDates.includes(myDate)) {
                            continue;
                        }

                        // Se la prenotazione esistente è ricorrente, verifica che coincida il giorno della settimana
                        if (isBRecurring) {
                            const dayOfWeek = moment(myDate, 'YYYY-MM-DD').day();
                            if (!b.recurrenceDays.map(Number).includes(dayOfWeek)) {
                                continue;
                            }
                        }

                        // Controllo sovrapposizione oraria: startA < endB && endA > startB
                        if (startTime < b.endTime && endTime > b.startTime) {
                            return {
                                hasConflict: true,
                                date: myDate,
                                space: commonSpaces.join(', '),
                                status: b.status,
                                existingTimes: `${b.startTime} - ${b.endTime}`,
                                eventName: b.eventName || ''
                            };
                        }
                    }
                }
            }

            return { hasConflict: false };
        } catch (error) {
            console.error("Errore nel controllo disponibilità:", error);
            toast.error("Errore durante la verifica disponibilità. Riprova tra poco.", {
                position: "top-center",
                autoClose: 5000,
                theme: "colored"
            });
            return { hasConflict: true, error: true };
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!eventName || selectedSpaces.length === 0 || !startDate || !startTime || !endTime) {
            toast.warn("Compila tutti i campi obbligatori! 📝");
            if (selectedSpaces.length === 0) { toast.warn("Non hai selezionato nessuno spazio🚧"); }
            return;
        }

        if (bookingType !== 'single' && !endDate) {
            toast.warn("Per prenotazioni multiple inserisci la data di fine.");
            return;
        }

        if (bookingType === 'recurring' && selectedWeekDays.length === 0) {
            toast.warn("Seleziona almeno un giorno della settimana! 📅");
            return;
        }

        if (bookingType !== 'single' && startDate > endDate) {
            toast.error("La data di inizio non può essere dopo la fine! 📅");
            return;
        }

        if (startTime >= endTime) { toast.error("Orario non valido (Inizio deve essere prima della Fine). ⏰"); return; }

        if (!termsAccepted) { toast.warn("Devi accettare le clausole 📝"); return; }

        setIsSubmitting(true);

        const availability = await checkAvailability();
        if (availability.hasConflict) {
            setIsSubmitting(false);
            if (!availability.error) {
                const formattedDate = moment(availability.date, 'YYYY-MM-DD').format('DD/MM/YYYY');
                const statusLabel = availability.status === 'confirmed' ? 'già confermata' : 'in attesa di approvazione';
                toast.error(
                    `⛔ Spazio occupato: "${availability.space}" ha già una prenotazione ${statusLabel} il ${formattedDate} dalle ${availability.existingTimes}!`,
                    {
                        position: "top-center",
                        autoClose: 6000,
                        theme: "colored"
                    }
                );
            }
            return;
        }

        try {
            const bookingData = {
                userId: user.uid,
                userEmail: user.email,
                eventName: eventName,
                spaceId: selectedSpaces.join(", "),
                description: description,
                adminNotes: adminNotes,

                // Nuovi campi per gestire i tipi
                bookingType: bookingType, // 'single', 'consecutive', 'recurring'
                recurrenceDays: bookingType === 'recurring' ? selectedWeekDays : null, // Salviamo i giorni es. [1, 3]
                isMultiDay: bookingType !== 'single', // Manteniamo per retro-compatibilità se serve

                startDate: startDate,
                endDate: bookingType === 'single' ? startDate : endDate,
                startTime: startTime,
                endTime: endTime,
                status: "pending",
                createdAt: new Date()
            };

            await addDoc(collection(db, "bookings"), bookingData);

            // Fetch userData statically to send in the email
            try {
                const userDoc = await getDoc(doc(db, "users", user.uid));
                const userDetails = userDoc.exists() ? userDoc.data() : null;

                // Fetch all admins and superusers
                const usersRef = collection(db, "users");
                const qAdmins = query(usersRef, where("role", "in", ["admin", "superuser"]));
                const adminSnap = await getDocs(qAdmins);
                const adminEmails = [];
                adminSnap.forEach(d => {
                    const data = d.data();
                    if (data.email) adminEmails.push(data.email);
                });

                sendNewBookingEmail(bookingData, userDetails, adminEmails);
            } catch (e) { console.error("Errore nell'invio email admin:", e); }

            toast.success("Richiesta inviata con successo! 🚀 reindirizzamento...");
            onBack();

        } catch (error) {
            console.error("Errore:", error);
            toast.error("Errore nell'invio. Riprova più tardi. ❌");
            setIsSubmitting(false);
        }
    };

    return (
        <div style={{

            minHeight: '100dvh', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '15px', fontFamily: "'Poppins', sans-serif"
        }}>

            <div className="glass-panel responsive-padding" style={{ width: '100%', maxWidth: '700px', padding: '40px', borderRadius: '30px', boxSizing: 'border-box' }}>

                <div className="responsive-booking-nav" style={{ display: 'flex', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid rgba(0,0,0,0.1)', paddingBottom: '15px', justifyContent: 'space-between', flexWrap: 'wrap', gap: '15px' }}>
                    <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'white', padding: '10px 25px', borderRadius: '30px', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#222', fontWeight: 'bold', boxShadow: '0 4px 10px rgba(0,0,0,0.1)', transition: 'transform 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                        <FaArrowLeft /> Torna alla Home
                    </button>
                    <h2 style={{ margin: 0, color: '#222', fontSize: '22px' }}>Nuova Prenotazione</h2>
                </div>

                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>

                    <div><label style={{ fontSize: '13px', fontWeight: 'bold', color: '#444' }}>Nome Attività o Evento *</label><input className="modern-input" type="text" value={eventName} onChange={(e) => setEventName(e.target.value)} /></div>

                    <div>
                        <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#444', marginBottom: '8px', display: 'block' }}>Quali spazi ti servono? *</label>
                        <div className="responsive-booking-spaces" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                            {spacesOptions.map((space) => {
                                const isSelected = selectedSpaces.includes(space);
                                return (
                                    <button key={space} type="button" onClick={() => toggleSpace(space)} style={{ padding: '8px 16px', borderRadius: '20px', border: isSelected ? '2px solid #007bff' : '1px solid rgba(0,0,0,0.1)', background: isSelected ? '#e3f2fd' : 'rgba(255,255,255,0.6)', color: isSelected ? '#007bff' : '#555', fontWeight: isSelected ? 'bold' : 'normal', cursor: 'pointer', fontSize: '13px', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        {isSelected && <FaCheck size={10} />} {space}
                                    </button>
                                )
                            })}
                        </div>
                    </div>

                    <div><label style={{ fontSize: '13px', fontWeight: 'bold', color: '#444' }}>Descrizione (Pubblica)</label><textarea className="modern-input" rows="2" placeholder="Descrivi brevemente l'attività..." value={description} onChange={(e) => setDescription(e.target.value)} style={{ fontFamily: 'Poppins' }} /></div>

                    {/* SEZIONE DATE E ORARI */}
                    <div style={{ background: 'rgba(255,255,255,0.5)', padding: '15px', borderRadius: '15px', border: '1px solid rgba(255,255,255,0.8)', boxSizing: 'border-box' }}>

                        {/* SELETTORE TIPO PRENOTAZIONE */}
                        <div className="responsive-booking-types" style={{ display: 'flex', gap: '5px', marginBottom: '15px', background: '#e0e0e0', padding: '5px', borderRadius: '25px', flexWrap: 'wrap' }}>
                            <button type="button" onClick={() => setBookingType('single')} style={{ flex: 1, border: 'none', background: bookingType === 'single' ? 'white' : 'transparent', borderRadius: '20px', padding: '8px', fontSize: '13px', fontWeight: 'bold', color: bookingType === 'single' ? '#007bff' : '#666', cursor: 'pointer', boxShadow: bookingType === 'single' ? '0 2px 5px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.3s' }}>
                                <FaCalendarDay style={{ marginRight: '5px' }} /> Singolo
                            </button>
                            <button type="button" onClick={() => setBookingType('consecutive')} style={{ flex: 1, border: 'none', background: bookingType === 'consecutive' ? 'white' : 'transparent', borderRadius: '20px', padding: '8px', fontSize: '13px', fontWeight: 'bold', color: bookingType === 'consecutive' ? '#007bff' : '#666', cursor: 'pointer', boxShadow: bookingType === 'consecutive' ? '0 2px 5px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.3s' }}>
                                <FaCalendarAlt style={{ marginRight: '5px' }} /> Consecutivi
                            </button>
                            <button type="button" onClick={() => setBookingType('recurring')} style={{ flex: 1, border: 'none', background: bookingType === 'recurring' ? 'white' : 'transparent', borderRadius: '20px', padding: '8px', fontSize: '13px', fontWeight: 'bold', color: bookingType === 'recurring' ? '#007bff' : '#666', cursor: 'pointer', boxShadow: bookingType === 'recurring' ? '0 2px 5px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.3s' }}>
                                <FaRedo style={{ marginRight: '5px' }} /> Ricorrente
                            </button>
                        </div>

                        {/* CAMPI DATA */}
                        <div className="responsive-booking-dates" style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
                            <div style={{ flex: 1, minWidth: '0', maxWidth: '100%', overflow: 'hidden' }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#444' }}>
                                    {bookingType === 'single' ? "Data *" : "Da data *"}
                                </label>
                                <div style={{ position: 'relative', width: '100%', boxSizing: 'border-box' }}>
                                    <FaCalendarAlt style={{ position: 'absolute', top: '12px', left: '10px', color: '#666' }} />
                                    <input type="date" className="modern-input" style={{ paddingLeft: '35px', width: '100%' }} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                                </div>
                            </div>
                            {bookingType !== 'single' && (
                                <div style={{ flex: 1, minWidth: '0', maxWidth: '100%', overflow: 'hidden' }}>
                                    <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#444' }}>A data *</label>
                                    <div style={{ position: 'relative', width: '100%', boxSizing: 'border-box' }}>
                                        <FaCalendarAlt style={{ position: 'absolute', top: '12px', left: '10px', color: '#666' }} />
                                        <input type="date" className="modern-input" style={{ paddingLeft: '35px', width: '100%' }} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* SELETTORE GIORNI SETTIMANA (SOLO SE RICORRENTE) */}
                        {bookingType === 'recurring' && (
                            <div style={{ marginTop: '15px', animation: 'fadeIn 0.5s' }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#444', display: 'block', marginBottom: '5px' }}>Ripeti ogni: *</label>
                                <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                    {weekDaysOptions.map(day => (
                                        <button
                                            key={day.id}
                                            type="button"
                                            onClick={() => toggleWeekDay(day.id)}
                                            style={{
                                                padding: '6px 12px',
                                                borderRadius: '15px',
                                                border: selectedWeekDays.includes(day.id) ? '2px solid #28a745' : '1px solid #ccc',
                                                background: selectedWeekDays.includes(day.id) ? '#d4edda' : 'white',
                                                color: selectedWeekDays.includes(day.id) ? '#155724' : '#555',
                                                cursor: 'pointer', fontSize: '12px', fontWeight: 'bold'
                                            }}
                                        >
                                            {day.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* ORARI */}
                        <div className="responsive-booking-dates" style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', marginTop: '15px' }}>
                            <div style={{ flex: 1, minWidth: '0', maxWidth: '100%', overflow: 'hidden' }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#444' }}>Ora Inizio *</label>
                                <div style={{ position: 'relative', width: '100%', boxSizing: 'border-box' }}>
                                    <FaClock style={{ position: 'absolute', top: '12px', left: '10px', color: '#666' }} />
                                    <input type="time" className="modern-input" style={{ paddingLeft: '35px', width: '100%' }} value={startTime} onChange={(e) => setStartTime(e.target.value)} />
                                </div>
                            </div>
                            <div style={{ flex: 1, minWidth: '0', maxWidth: '100%', overflow: 'hidden' }}>
                                <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#444' }}>Ora Fine *</label>
                                <div style={{ position: 'relative', width: '100%', boxSizing: 'border-box' }}>
                                    <FaClock style={{ position: 'absolute', top: '12px', left: '10px', color: '#666' }} />
                                    <input type="time" className="modern-input" style={{ paddingLeft: '35px', width: '100%' }} value={endTime} onChange={(e) => setEndTime(e.target.value)} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{ background: '#fff3cd', padding: '15px', borderRadius: '10px', border: '1px solid #ffeeba', boxSizing: 'border-box' }}>
                        <label style={{ fontSize: '13px', fontWeight: 'bold', color: '#856404', display: 'flex', alignItems: 'center', gap: '5px' }}><FaStickyNote /> Richieste aggiuntive</label>
                        <textarea className="modern-input" rows="2" placeholder="Scrivi qui le tue richieste..." value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} style={{ fontFamily: 'Poppins', marginBottom: 0, background: 'rgba(255,255,255,0.8)' }} />
                    </div>

                    {/* CLAUSOLA ACCETTAZIONE */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(255,255,255,0.4)', padding: '10px', borderRadius: '10px', boxSizing: 'border-box' }}>
                        <input
                            type="checkbox"
                            id="terms"
                            checked={termsAccepted}
                            onChange={(e) => setTermsAccepted(e.target.checked)}
                            style={{ width: '18px', height: '18px', cursor: 'pointer', accentColor: '#28a745' }}
                        />
                        <label htmlFor="terms" style={{ fontSize: '13px', color: '#333', cursor: 'pointer', userSelect: 'none' }}>
                            Dichiaro di aver letto e accettato il <a href="https://www.civiclab.it/disciplinare-interno" target="_blank" rel="noopener noreferrer" style={{ color: '#007bff', textDecoration: 'underline' }}>Regolamento per l'utilizzo degli spazi</a>.
                        </label>
                    </div>

                    {/* BOTTONE INVIO */}
                    <button
                        type="submit"
                        className="primary-button"
                        style={{
                            background: termsAccepted ? '#28a745' : '#ccc',
                            marginTop: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px',
                            cursor: termsAccepted ? 'pointer' : 'not-allowed'
                        }}
                        disabled={isSubmitting || !termsAccepted}
                    >
                        {isSubmitting ? "Invio in corso..." : <><FaCheck /> Invia Richiesta di Prenotazione</>}
                    </button>

                </form>

            </div>
        </div>
    );
}

export default BookingForm;